/**
 * FullviewConstellationDataBuilder
 *
 * Sprint 49: Wraps the existing ConstellationDataBuilder with a validation
 * layer for the fullview (flat force-directed) constellation visualizer.
 * Validates node counts, edge presence, and entity type diversity before
 * rendering.
 *
 * Follows OOA/OOD: Single Responsibility — validation and build orchestration.
 */

import { ConstellationDataBuilder } from './constellation-data-builder';
import type {
    ConstellationData,
    ConstellationNode,
} from './constellation-data-builder';
import type { CanisterClientInterface } from '../canister-client';

// ---------------------------------------------------------------------------
// Interfaces
// ---------------------------------------------------------------------------

export interface ValidationResult {
    valid: boolean;
    nodeCount: number;
    edgeCount: number;
    entityTypes: string[];
    warnings: string[];
    errors: string[];
}

export interface BuildAndValidateResult {
    data: ConstellationData;
    validation: ValidationResult;
}

// ---------------------------------------------------------------------------
// FullviewConstellationDataBuilder
// ---------------------------------------------------------------------------

export class FullviewConstellationDataBuilder {
    private builder: ConstellationDataBuilder;

    constructor(canisterClient: CanisterClientInterface) {
        this.builder = new ConstellationDataBuilder(canisterClient);
    }

    /**
     * Validate constellation data for fullview rendering suitability.
     */
    validate(data: ConstellationData): ValidationResult {
        const errors: string[] = [];
        const warnings: string[] = [];

        const nodeCount = data.nodes.length;
        const edgeCount = data.edges.length;

        // Collect unique entity types
        const typeCounts = new Map<string, number>();
        for (const node of data.nodes) {
            typeCounts.set(
                node.entityType,
                (typeCounts.get(node.entityType) || 0) + 1,
            );
        }
        const entityTypes = Array.from(typeCounts.keys()).sort();

        // --- Errors ---
        if (nodeCount < 2) {
            errors.push(
                `Not enough entities to create a map. Found ${nodeCount} entit${nodeCount === 1 ? 'y' : 'ies'} — need at least 2.`,
            );
        }

        // --- Warnings ---
        if (edgeCount === 0 && nodeCount >= 2) {
            warnings.push(
                'No relationships found between entities. The map will show disconnected nodes. ' +
                'Consider adding an Integrations sheet or relationship data.',
            );
        }

        if (entityTypes.length === 1) {
            warnings.push(
                `Only one entity type found (${entityTypes[0]}). A richer map needs multiple entity types ` +
                '(e.g., applications, services, infrastructure).',
            );
        }

        // --- Info: entity type coverage ---
        if (entityTypes.length > 1) {
            const summary = entityTypes
                .map((t) => `${t} (${typeCounts.get(t)})`)
                .join(', ');
            warnings.push(`Entity type coverage: ${summary}`);
        }

        return {
            valid: errors.length === 0,
            nodeCount,
            edgeCount,
            entityTypes,
            warnings,
            errors,
        };
    }

    /**
     * Build constellation data from canister, then validate.
     */
    async buildAndValidate(sessionId?: string): Promise<BuildAndValidateResult> {
        const data = await this.builder.build(sessionId);
        const validation = this.validate(data);

        if (!validation.valid) {
            const advice = validation.errors.join('\n');
            throw new Error(
                `Constellation validation failed:\n${advice}\n\n` +
                'Tip: Ensure your import session includes at least 2 entities across ' +
                'multiple sheets (e.g., Applications, Infrastructure, Services).',
            );
        }

        return { data, validation };
    }
}

// ---------------------------------------------------------------------------
// Convenience function export  (Sprint 49)
// ---------------------------------------------------------------------------

/**
 * Build and validate constellation data in one call.
 * Returns `{ data, validation }` or throws with actionable advice.
 */
export async function buildAndValidate(
    canisterClient: CanisterClientInterface,
    sessionId?: string,
): Promise<BuildAndValidateResult> {
    const builder = new FullviewConstellationDataBuilder(canisterClient);
    return builder.buildAndValidate(sessionId);
}
